import React, { Fragment } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useTranslation } from "react-i18next";

import { getDashboardConfig } from "../../config";
import { demoLogout } from "../../utils/session";
import { fonts } from "../../themes/theme";
import BrandMark from "./BrandMark";

export const SIDEBAR_WIDTH = 256;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

/**
 * Navegacion lateral (tema web psicologo, sin iconos). Items agrupados en
 * categorias (radiografia / seguimiento / negocio) como el HTML de referencia:
 * etiqueta de grupo + numero pequeño + nombre. Fondo navy, colapsable.
 */
function Sidebar({ collapsed, onToggle }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
    const { views: dashboardViews, groups: navGroups } = getDashboardConfig();

    let counter = 0;

    return (
        <Box
            component="aside"
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                height: "100vh",
                width,
                bgcolor: "navy.main",
                display: "flex",
                flexDirection: "column",
                transition: "width 0.3s ease",
                zIndex: 1000,
            }}
        >
            <Box
                sx={{
                    height: 80,
                    px: collapsed ? 0 : 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                <IconButton onClick={onToggle} aria-label="Contraer/expandir menu" sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(61,79,95,0.3)" } }}>
                    <MenuRoundedIcon />
                </IconButton>
                {!collapsed && <BrandMark onClick={onToggle} />}
            </Box>

            <Box component="nav" sx={{ flex: 1, px: 1.5, pt: 0.5, display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden" }}>
                {navGroups.map((group) => {
                    const items = dashboardViews.filter((v) => v.group === group);
                    if (!items.length) return null;
                    return (
                        <Fragment key={group}>
                            {!collapsed && (
                                <Typography
                                    sx={{
                                        px: 1.5,
                                        pt: 1.75,
                                        pb: 0.75,
                                        fontSize: 10.5,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.12em",
                                        color: "rgba(255,255,255,0.4)",
                                    }}
                                >
                                    {t(`sidebar.groups.${group}`)}
                                </Typography>
                            )}
                            {items.map(({ key, path }) => {
                                counter += 1;
                                const num = counter;
                                return (
                                    <Tooltip key={key} title={collapsed ? t(`nav.${key}`) : ""} placement="right">
                                        <Box
                                            component={NavLink}
                                            to={path}
                                            end={path === "/"}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.25,
                                                px: collapsed ? 0 : 1.5,
                                                py: 1,
                                                mb: 0.25,
                                                justifyContent: collapsed ? "center" : "flex-start",
                                                borderRadius: "10px",
                                                color: "rgba(255,255,255,0.72)",
                                                textDecoration: "none",
                                                transition: "background-color 0.16s ease, color 0.16s ease",
                                                "&:hover": { bgcolor: "rgba(61,79,95,0.25)", color: "#fff" },
                                                "&.active": { color: "#fff" },
                                            }}
                                        >
                                            <Box component="span" sx={{ fontSize: 11, width: 14, flexShrink: 0, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>
                                                {num}
                                            </Box>
                                            {!collapsed && (
                                                <Typography sx={{ fontSize: 13.5 }} noWrap>
                                                    {t(`nav.${key}`)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Fragment>
                    );
                })}
            </Box>

            <Box
                sx={{
                    borderTop: "1px solid rgba(61,79,95,0.4)",
                    px: collapsed ? 1 : 2,
                    py: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                }}
            >
                <Tooltip title={collapsed ? t("sidebar.logout") : ""} placement="right">
                    <Box
                        component="button"
                        type="button"
                        onClick={() => demoLogout(navigate)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: collapsed ? "center" : "flex-start",
                            gap: 1.25,
                            width: "100%",
                            border: 0,
                            cursor: "pointer",
                            bgcolor: "transparent",
                            color: "rgba(255,255,255,0.72)",
                            borderRadius: "10px",
                            px: collapsed ? 0 : 1.5,
                            py: 1,
                            fontFamily: fonts.body,
                            fontSize: 13.5,
                            transition: "background-color 0.16s ease, color 0.16s ease",
                            "&:hover": { bgcolor: "rgba(61,79,95,0.25)", color: "#fff" },
                        }}
                    >
                        <LogoutRoundedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                        {!collapsed && <span>{t("sidebar.logout")}</span>}
                    </Box>
                </Tooltip>
                {!collapsed && (
                    <Typography sx={{ px: 1.5, fontSize: 11, color: "rgba(173,202,214,0.7)", lineHeight: 1.5 }}>
                        {t("sidebar.footer")}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export default Sidebar;
